import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, TrendingUp, TrendingDown, Minus, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function ScoutingReport({ childId, childName }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/scouting/report/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReport(res.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, [childId]);

  if (loading) return <div className="animate-pulse h-32 bg-zinc-900/50 rounded-xl" />;
  if (!report || !report.test_comparisons?.length) return null;

  const gradeColor = (g) => g >= 4 ? 'text-green-400' : g >= 3 ? 'text-yellow-400' : 'text-red-400';
  const gradeBg = (g) => g >= 4 ? 'bg-green-500/20' : g >= 3 ? 'bg-yellow-500/20' : 'bg-red-500/20';

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="scouting-report">
      <div 
        className="p-5 flex items-center justify-between cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#02b5ff]/10 flex items-center justify-center">
            <FileText className="h-5 w-5 text-[#02b5ff]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Scouting Report</h3>
            <p className="text-xs text-zinc-400">
              {report.total_tests} tests analysed | Overall: <span className={gradeColor(report.overall_grade)}>{report.overall_label}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1 rounded-full text-xs font-bold ${gradeBg(report.overall_grade)} ${gradeColor(report.overall_grade)}`}>
            {report.overall_grade}/5
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-zinc-800">
          {/* Strengths & Weaknesses */}
          <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-green-400 uppercase tracking-wider font-semibold mb-2">Strengths</p>
              {report.strengths?.length > 0 ? report.strengths.map((s, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <TrendingUp className="h-3.5 w-3.5 text-green-400" />
                  <span className="text-xs text-zinc-300">{s.test}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 ml-auto">Top {100 - s.percentile}%</span>
                </div>
              )) : <p className="text-xs text-zinc-600 italic">No standout strengths yet</p>}
            </div>
            <div>
              <p className="text-[10px] text-red-400 uppercase tracking-wider font-semibold mb-2">Development Areas</p>
              {report.weaknesses?.length > 0 ? report.weaknesses.map((w, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <TrendingDown className="h-3.5 w-3.5 text-red-400" />
                  <span className="text-xs text-zinc-300">{w.test}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 ml-auto">Bottom {w.percentile}%</span>
                </div>
              )) : <p className="text-xs text-zinc-600 italic">No significant weaknesses</p>}
            </div>
          </div>

          {/* Detailed Test Comparisons */}
          <div className="px-5 pb-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3">Test Breakdown vs {report.gender?.charAt(0).toUpperCase() + report.gender?.slice(1)} Benchmarks ({report.age_group})</p>
            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {report.test_comparisons.map((test, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 bg-zinc-800/40 rounded-lg hover:bg-zinc-800/60 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{test.test_name}</p>
                    <p className="text-[11px] text-zinc-500">{test.result_value} {test.unit}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Grade */}
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold ${gradeBg(test.grade)} ${gradeColor(test.grade)}`}>
                      {test.grade}
                    </div>
                    {/* National Percentile */}
                    <div className="text-right w-14">
                      <p className="text-[10px] text-zinc-500">National</p>
                      <p className="text-xs font-semibold text-zinc-300">{test.national_percentile}th</p>
                    </div>
                    {/* vs Team */}
                    {test.team_average && (
                      <div className="text-right w-14">
                        <p className="text-[10px] text-zinc-500">vs Team</p>
                        <div className="flex items-center justify-end gap-0.5">
                          {test.vs_team === 'above' ? (
                            <TrendingUp className="h-3 w-3 text-green-400" />
                          ) : test.vs_team === 'below' ? (
                            <TrendingDown className="h-3 w-3 text-red-400" />
                          ) : (
                            <Minus className="h-3 w-3 text-zinc-400" />
                          )}
                          <span className={`text-xs font-semibold ${
                            test.vs_team === 'above' ? 'text-green-400' : test.vs_team === 'below' ? 'text-red-400' : 'text-zinc-400'
                          }`}>{test.vs_team_pct > 0 ? '+' : ''}{test.vs_team_pct}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-between bg-zinc-900/30">
            <p className="text-[10px] text-zinc-600">Generated {new Date(report.generated_at).toLocaleDateString()}</p>
            <p className="text-[10px] text-zinc-500">Benchmarks: {report.gender} ({report.age_group})</p>
          </div>
        </div>
      )}
    </div>
  );
}
